// Responsive breakpoint hooks. Wraps MUI's `useMediaQuery` so every page can
// branch on viewport size without re-importing `theme.breakpoints` everywhere.
//
// MUI v5 defaults:
//   xs: 0–599   sm: 600–899   md: 900–1199   lg: 1200–1535   xl: 1536+
//
// Convention used in this codebase:
//   - "mobile"  = width < md (i.e. xs + sm)
//   - "tablet"  = sm <= width < lg
//   - "desktop" = width >= md
//   - "narrow"  = xs only (< 600 — phone portrait)

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

export function useIsNarrow(): boolean {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('sm'));
}

export function useIsMobile(): boolean {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('md'));
}

export function useIsTablet(): boolean {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.between('sm', 'lg'));
}

export function useIsDesktop(): boolean {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.up('md'));
}

export function useIsLarge(): boolean {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.up('lg'));
}

/**
 * Honors the user's OS-level reduced-motion preference. Use to gate any JS-
 * driven animation (e.g. `transform`-based hover effects) so we don't bypass
 * the global CSS rule with a programmatic style.
 */
export function usePrefersReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)');
}
